import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from 'store'
import { OtherText, ParagraphText } from 'styling/Typography'
import { Flex } from 'styling/GlobalStyles'

interface IPieChartData {
  name: string
  value: number
  color?: string | { start: string; end: string }
}

interface IPieChart {
  data: IPieChartData[]
  size?: number
  showLabels?: boolean
  showValues?: boolean
}

export const PieChart: React.FC<IPieChart> = ({
  data,
  size = 200,
  showLabels = true,
  showValues = true,
}) => {
  const [hoveredSlice, setHoveredSlice] = useState<number | null>(null)
  const { darkMode } = useSelector((state: RootState) => state.globals)

  // Calculate total value
  const total = data.reduce((sum, item) => sum + item.value, 0)

  // Default colors if not provided
  const defaultColors = darkMode
    ? [
        { start: '#A3261B', end: '#CF1300' }, // red
        { start: '#146600', end: '#1B6F00' }, // green
        { start: '#806500', end: '#8C6F00' }, // yellow
        { start: '#236A99', end: '#2980B9' }, // blue
        { start: '#852EAB', end: '#8E24AA' }, // purple
        { start: '#FF6A1A', end: '#FF5722' }, // orange
        { start: '#00695C', end: '#00695C' }, // teal (kept consistent, already very dark)
        { start: '#D74E22', end: '#D84315' }, // deep orange
        { start: '#3AAE3E', end: '#388E3C' }, // dark green
        { start: '#1F77D4', end: '#1976D2' }, // dark blue
      ]
    : [
        { start: '#FF6B6B', end: '#FF5252' }, // light red
        { start: '#4CAF50', end: '#45A049' }, // light green
        { start: '#FFC107', end: '#FFB300' }, // light yellow
        { start: '#2196F3', end: '#1976D2' }, // light blue
        { start: '#9C27B0', end: '#8E24AA' }, // light purple
        { start: '#FF9800', end: '#F57C00' }, // light orange
        { start: '#009688', end: '#00796B' }, // light teal
        { start: '#FF5722', end: '#E64A19' }, // light deep orange
        { start: '#8BC34A', end: '#7CB342' }, // light green
        { start: '#03A9F4', end: '#0288D1' }, // light blue
      ]

  // Generate pie chart slices
  const generateSlices = () => {
    let currentAngle = 0

    return data.map((item, index) => {
      const percentage = (item.value / total) * 100
      const angle = (percentage / 100) * 360
      const colorConfig = item.color || defaultColors[index % defaultColors.length]
      const color = typeof colorConfig === 'string' ? colorConfig : colorConfig.start

      // Calculate SVG path for pie slice
      const radius = size / 2 - 10
      const centerX = size / 2
      const centerY = size / 2

      const startAngleRad = (currentAngle * Math.PI) / 180
      const endAngleRad = ((currentAngle + angle) * Math.PI) / 180

      const x1 = centerX + radius * Math.cos(startAngleRad)
      const y1 = centerY + radius * Math.sin(startAngleRad)
      const x2 = centerX + radius * Math.cos(endAngleRad)
      const y2 = centerY + radius * Math.sin(endAngleRad)

      const largeArcFlag = angle > 180 ? 1 : 0

      const pathData = [
        `M ${centerX} ${centerY}`,
        `L ${x1} ${y1}`,
        `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
        'Z',
      ].join(' ')

      const slice = {
        pathData,
        color,
        colorConfig,
        percentage,
        name: item.name,
        value: item.value,
        index,
      }

      currentAngle += angle
      return slice
    })
  }

  const slices = generateSlices()

  return (
    <Flex column gap='15px' center>
      <div style={{ position: 'relative' }}>
        <svg width={size} height={size}>
          {slices.map((slice, index) => (
            <g key={index}>
              {typeof slice.colorConfig === 'object' && (
                <defs>
                  <linearGradient id={`gradient-${index}`} x1='0%' y1='0%' x2='100%' y2='0%'>
                    <stop offset='32%' stopColor={slice.colorConfig.start} />
                    <stop offset='81%' stopColor={slice.colorConfig.end} />
                  </linearGradient>
                </defs>
              )}
              <path
                d={slice.pathData}
                fill={
                  typeof slice.colorConfig === 'object' ? `url(#gradient-${index})` : slice.color
                }
                opacity={hoveredSlice === index ? 0.8 : 1}
                stroke={darkMode ? '#2a2a2a' : '#fff'}
                strokeWidth='2'
                onMouseEnter={() => setHoveredSlice(index)}
                onMouseLeave={() => setHoveredSlice(null)}
                style={{ cursor: 'pointer', transition: 'opacity 0.2s' }}
              />
            </g>
          ))}

          {/* Center circle for donut effect (optional) */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={size / 3.5}
            fill={darkMode ? '#2a2a2a' : '#f5f5f5'}
            stroke={darkMode ? '#444' : '#ddd'}
            strokeWidth='1'
          />

          {/* Center text showing total */}
          <text
            x='50%'
            y='50%'
            dominantBaseline='middle'
            textAnchor='middle'
            fontSize='14'
            fontWeight='bold'
            fill={darkMode ? '#fff' : '#46425'}
          >
            {total}
          </text>
        </svg>

        {/* Tooltip */}
        {hoveredSlice !== null && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: darkMode ? '#333' : '#fff',
              border: `1px solid ${darkMode ? '#555' : '#ddd'}`,
              borderRadius: '8px',
              padding: '8px 12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              zIndex: 10,
              pointerEvents: 'none',
            }}
          >
            <OtherText
              style={{
                color: darkMode ? '#fff' : '#46425',
                fontWeight: 'bold',
                marginBottom: '4px',
              }}
            >
              {slices[hoveredSlice].name}
            </OtherText>
            <OtherText
              style={{
                color: darkMode ? '#ccc' : '#666',
                fontSize: '12px',
              }}
            >
              {slices[hoveredSlice].value} ({slices[hoveredSlice].percentage.toFixed(1)}%)
            </OtherText>
          </div>
        )}
      </div>

      {/* Legend */}
      {showLabels && (
        <Flex column gap='8px' style={{ width: '100%' }}>
          {slices.map((slice, index) => (
            <Flex key={index} center gap='8px'>
              <div
                style={{
                  width: '12px',
                  height: '12px',
                  backgroundColor:
                    typeof slice.colorConfig === 'object' ? slice.colorConfig.start : slice.color,
                  borderRadius: '2px',
                  border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
                }}
              />
              <Flex between style={{ flex: 1 }}>
                <ParagraphText
                  style={{
                    color: darkMode ? '#fff' : '#46425',
                    fontSize: '14px',
                  }}
                >
                  {slice.name}
                </ParagraphText>
                {showValues && (
                  <OtherText
                    style={{
                      color: darkMode ? '#ccc' : '#666',
                      fontSize: '12px',
                    }}
                  >
                    {slice.value} ({slice.percentage.toFixed(1)}%)
                  </OtherText>
                )}
              </Flex>
            </Flex>
          ))}
        </Flex>
      )}
    </Flex>
  )
}
